import React from "react";

const CHUNK_RELOAD_KEY = "chunk-reload-attempted";

function canUseSessionStorage() {
  return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
}

function getReloadAttempted(storageKey) {
  if (!canUseSessionStorage()) return false;

  try {
    return window.sessionStorage.getItem(storageKey) === "true";
  } catch {
    return false;
  }
}

function setReloadAttempted(storageKey, value) {
  if (!canUseSessionStorage()) return;

  try {
    if (value) {
      window.sessionStorage.setItem(storageKey, "true");
    } else {
      window.sessionStorage.removeItem(storageKey);
    }
  } catch {
    // Ignore sessionStorage errors (e.g. privacy mode restrictions).
  }
}

function isChunkLoadError(error) {
  const message = error?.message || "";
  const name = error?.name || "";

  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

export function lazyWithRetry(importFunc, options = {}) {
  const { storageKey = CHUNK_RELOAD_KEY } = options;

  return React.lazy(async () => {
    try {
      const module = await importFunc();

      // Clear the flag after a successful lazy import so future deployments can retry once.
      setReloadAttempted(storageKey, false);
      return module;
    } catch (error) {
      const hasReloaded = getReloadAttempted(storageKey);

      if (isChunkLoadError(error) && !hasReloaded) {
        setReloadAttempted(storageKey, true);
        window.location.reload();

        // Keep Suspense fallback visible while the browser reloads.
        return new Promise(() => {});
      }

      throw error;
    }
  });
}